// ============================================================================
// DANCE STUDIO CORE - Enhanced API Service
// ============================================================================
// This file provides enhanced API calls with caching, retry logic, and error handling

import { apiCache, CacheKeys, CacheTTL } from './cache'

interface ApiResponse<T> {
  data?: T
  success?: boolean
  error?: string
}

interface RetryConfig {
  maxRetries: number
  baseDelay: number
  maxDelay: number
  backoffMultiplier: number
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 2,
  baseDelay: 100, // 0.1 second
  maxDelay: 1000, // 1 seconds
  backoffMultiplier: 2
}

class ApiService {
  private baseUrl = '/api'

  /**
   * Make API call with retry logic and caching
   */
  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    cacheKey?: string,
    ttl?: number,
    retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG
  ): Promise<T> {
    const url = `${this.baseUrl}/${endpoint}`
    
    // If cache key provided, try to get from cache first
    if (cacheKey) {
      return apiCache.get(
        cacheKey,
        () => this.executeRequest<T>(url, options, retryConfig),
        ttl || CacheTTL.DEFAULT
      )
    }

    // Direct request without caching
    return this.executeRequest<T>(url, options, retryConfig)
  }

  /**
   * Execute request with retry logic
   */
  private async executeRequest<T>(
    url: string,
    options: RequestInit,
    retryConfig: RetryConfig
  ): Promise<T> {
    let lastError: Error | null = null

    for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            ...options.headers
          }
        })

        if (!response.ok) {
          // Get the actual error message from the server response
          const errorData = await response.json().catch(() => ({}))
          const serverErrorMessage = errorData.error || errorData.message || 'Request failed'
          
          // Handle different error types with server-specific messages
          if (response.status === 409) {
            // Duplicate enrollment - use server message
            const error = new Error(serverErrorMessage)
            ;(error as any).status = 409 // Mark as 409 error for retry logic
            throw error
          } else if (response.status === 503) {
            throw new Error(serverErrorMessage)
          } else if (response.status >= 500) {
            throw new Error(serverErrorMessage)
          } else if (response.status === 429) {
            throw new Error(serverErrorMessage)
          } else {
            throw new Error(serverErrorMessage)
          }
        }

        const result: ApiResponse<T> = await response.json()
        
        if (result.error) {
          throw new Error(result.error)
        }

        return result.data || result as T

      } catch (error) {
        lastError = error as Error
        
        // Don't retry on client errors (4xx) except 429
        if (error instanceof Error && (error.message.includes('status 4') || (error as any).status === 409)) {
          throw error
        }

        // Don't retry on last attempt
        if (attempt === retryConfig.maxRetries) {
          break
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          retryConfig.baseDelay * Math.pow(retryConfig.backoffMultiplier, attempt),
          retryConfig.maxDelay
        )

        console.warn(`API request failed (attempt ${attempt + 1}/${retryConfig.maxRetries + 1}), retrying in ${delay}ms:`, error)
        
        await this.sleep(delay)
      }
    }

    throw lastError || new Error('Request failed after all retries')
  }

  /**
   * Sleep utility for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Get schedules with caching
   */
  async getSchedules(classId?: number, forceRefresh: boolean = false): Promise<any[]> {
    const cacheKey = classId ? CacheKeys.SCHEDULES_BY_CLASS(classId) : CacheKeys.SCHEDULES
    
    if (forceRefresh) {
      // Force refresh by invalidating cache first
      apiCache.invalidate(cacheKey)
    }
    
    return this.request<any[]>(
      `classes.php${classId ? `?class_id=${classId}` : ''}`,
      { method: 'GET' },
      cacheKey,
      CacheTTL.SCHEDULES
    )
  }

  /**
   * Get enrollments with caching
   */
  async getEnrollments(status?: string, forceRefresh: boolean = false): Promise<any[]> {
    const cacheKey = status ? CacheKeys.ENROLLMENTS_BY_STATUS(status) : CacheKeys.ENROLLMENTS
    
    if (forceRefresh) {
      // Force refresh by invalidating cache first
      apiCache.invalidate(cacheKey)
    }
    
    return this.request<any[]>(
      `enrollments.php${status ? `?status=${status}` : ''}`,
      { method: 'GET' },
      cacheKey,
      CacheTTL.ENROLLMENTS
    )
  }


  /**
   * Create enrollment (no caching)
   */
  async createEnrollment(data: any): Promise<any> {
    const result = await this.request<any>(
      'enrollments.php',
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    )

    // Invalidate related caches
    this.invalidateRelatedCaches('enrollment')


    return result
  }

  /**
   * Update enrollment status (no caching)
   */
  async updateEnrollmentStatus(id: string, status: string): Promise<any> {
    const result = await this.request<any>(
      `enrollments.php?id=${id}`,
      {
        method: 'PUT',
        body: JSON.stringify({ status })
      }
    )

    // Invalidate related caches
    this.invalidateRelatedCaches('enrollment')


    return result
  }

  /**
   * Delete enrollment (no caching)
   */
  async deleteEnrollment(id: string): Promise<any> {
    const result = await this.request<any>(
      `enrollments.php?id=${id}`,
      { method: 'DELETE' }
    )

    // Invalidate related caches
    this.invalidateRelatedCaches('enrollment')


    return result
  }

  /**
   * Create schedule (no caching)
   */
  async createSchedule(data: any): Promise<any> {
    const result = await this.request<any>(
      'classes.php',
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    )

    // Invalidate related caches
    this.invalidateRelatedCaches('schedule')


    return result
  }

  /**
   * Update schedule (no caching)
   */
  async updateSchedule(id: number, data: any): Promise<any> {
    const result = await this.request<any>(
      `classes.php?id=${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data)
      }
    )

    // Invalidate related caches
    this.invalidateRelatedCaches('schedule')


    return result
  }

  /**
   * Delete schedule (no caching)
   */
  async deleteSchedule(id: number): Promise<any> {
    const result = await this.request<any>(
      `classes.php?id=${id}`,
      { method: 'DELETE' }
    )

    // Invalidate related caches
    this.invalidateRelatedCaches('schedule')


    return result
  }


  /**
   * Submit contact form (no caching)
   */
  async submitContact(data: {
    name: string
    email: string
    phone: string
    subject: string
    message: string
  }): Promise<any> {
    return this.request<any>(
      'contacts.php',
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    )
  }


  /**
   * Batch invalidate related caches
   */
  private invalidateRelatedCaches(operation: 'enrollment' | 'schedule'): void {
    switch (operation) {
      case 'enrollment':
        apiCache.invalidate('schedules')
        apiCache.invalidate('enrollments')
        break
      case 'schedule':
        apiCache.invalidate('schedules')
        apiCache.invalidate('enrollments')
        break
    }
  }
}

// Export singleton instance
export const apiService = new ApiService()
